import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { AddressString, UpdateFundPermissions, User } from 'types'

const LOCAL_STORAGE_KEY = 'applicationAuthState'

type Error = {
  code: number
}

export type ConnectData = {
  createdAt: null | string
  lastTopUp: null | string
}

type SignedInState = {
  account: `0x${string}`
  deadline: number
  error?: null
  isSignedIn: true
  signAcountPrivateKey: `0x${string}`
  signature: string
  userData: User
}

type NotSignedInState = {
  account: null
  deadline: null
  error: Error
  isSignedIn: false
  signAcountPrivateKey: null
  signature: null
  userData: null
}

type State = NotSignedInState | SignedInState

const initialState: State = {
  account: null,
  deadline: null,
  error: null,
  isSignedIn: false,
  signAcountPrivateKey: null,
  signature: null,
  userData: null,
} as State

const initialUserState: User = {
  createdAt: null,
  funds: [],
  id: '',
  invitations: [],
  lastTopUp: null,
  permissions: {
    funds: {},
    investor: '0x0',
    pools: {},
    ufarm: '0x0',
  },
  ref: '',
  sponsoredGas: false,
}

const getStateFromStorage = (key: string) => {
  try {
    const state = JSON.parse(localStorage.getItem(key)) as State
    return state
  } catch {
    return initialState
  }
}

export const persistedState = localStorage.getItem(LOCAL_STORAGE_KEY)
  ? getStateFromStorage(LOCAL_STORAGE_KEY)
  : initialState

export const sliceUser = createSlice({
  initialState: persistedState,
  name: 'user',
  reducers: {
    connect: (state, { payload }: PayloadAction<SignedInState>) => {
      state.isSignedIn = true
      state.account = payload.account
      state.signature = payload.signature
      state.deadline = payload.deadline
      state.signAcountPrivateKey = payload.signAcountPrivateKey
      state.userData = payload.userData
    },
    connectUnauthorized: (state, { payload }: PayloadAction<{ address: string; data: ConnectData }>) => {
      state.account = payload.address as AddressString
      state.userData = {
        ...initialUserState,
        createdAt: payload.data.createdAt,
        lastTopUp: payload.data.lastTopUp,
      }
    },
    disconnect: (state) => {
      state.isSignedIn = false
      state.account = null
      state.signature = null
      state.deadline = null
      state.signAcountPrivateKey = null
      state.userData = null
    },
    setError: (state, { payload }: PayloadAction<Error>) => {
      state.error = payload
    },
    setUser: (state, { payload }: PayloadAction<User>) => {
      state.userData = payload
    },
    updateFundPermissions: (state, { payload }: PayloadAction<UpdateFundPermissions>) => {
      state.userData.permissions.funds = { [payload.fundAddress]: payload.permissions.funds }
    },
  },
})
export const { connect, connectUnauthorized, disconnect, setError, setUser, updateFundPermissions } = sliceUser.actions

export const localStorageMiddleware = () => {
  return (next) => (action) => {
    if (action.type === 'user/connect') {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(action.payload))
    } else if (action.type === 'user/disconnect') {
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    }
    const result = next(action)
    return result
  }
}
